import type { RecordContext } from '../types/assistant.types';

export interface SuggestionChip {
  id: string;
  /** Short pill text. */
  label: string;
  /** The full prompt sent to the assistant when the chip is tapped. */
  message: string;
}

/**
 * FR-12 record-aware starter chips for the empty thread. Copy is phrased
 * against the assistant's real capabilities (lead lookup, note creation,
 * task creation, KB search). The lead variants say "this lead" because the
 * chat hook attaches the active record context to the send.
 */
const DEFAULT_CHIPS: SuggestionChip[] = [
  {
    id: 'default-summary-today',
    label: 'Summary for today',
    message: 'Give me a summary for today.',
  },
  {
    id: 'default-add-lead',
    label: 'Add Lead',
    message: 'Help me add a new lead.',
  },
  {
    id: 'default-add-participant',
    label: 'Add Participant',
    message: 'Help me add a new participant.',
  },
  {
    id: 'default-add-appointment',
    label: 'Add Appointment',
    message: 'Help me add a new appointment.',
  },
  {
    id: 'default-add-task',
    label: 'Add Task',
    message: 'Help me add a new task.',
  },
  {
    id: 'default-add-reminder',
    label: 'Add Reminder',
    message: 'Help me add a new reminder.',
  },
  {
    id: 'default-add-placement',
    label: 'Add Placement',
    message: 'Help me add a new placement.',
  },
  {
    id: 'default-add-invoice',
    label: 'Add Invoice',
    message: 'Help me add a new invoice.',
  },
];

const LEAD_CHIPS: SuggestionChip[] = [
  {
    id: 'lead-summary',
    label: 'Summarize this lead',
    message:
      'Summarize this lead for me: status, key details, and recent activity.',
  },
  {
    id: 'lead-follow-up',
    label: 'Draft a personalized follow-up',
    message:
      'Draft a personalized follow-up for this lead using their current details, interested programs, and recent notes.',
  },
  {
    id: 'lead-add-note',
    label: 'Add a note',
    message: 'Add a note on this lead that I reviewed it today.',
  },
];

/** Starter chips — Figma new-chat set, or lead-aware when a lead is in context. */
export function suggestionChips(
  recordContext: Pick<RecordContext, 'type' | 'id'> | null
): SuggestionChip[] {
  return recordContext?.type === 'lead' ? LEAD_CHIPS : DEFAULT_CHIPS;
}
